import { useState } from 'react';
import { Alert } from '@/components/States';
import { money, num, shortDate } from '@/lib/format';
import type { StatementPayload } from './types';
import { documentStatus } from './status';
import s from './SupplierStatementWorkspace.module.css';

export function SupplierStatementWorkspace({ d }: { d: StatementPayload }) {
  const [onlyReceived, setOnlyReceived] = useState(false);
  const docs = d.businessDocuments;
  const allGoods = docs.flatMap((doc) => (doc.goods || []).map((good) => ({ doc, good })));
  const receiptBasis = allGoods.length > 0 && allGoods.every(({ good }) => good.billingBasis === 'RECEIVED');
  const rows = onlyReceived && receiptBasis
    ? allGoods.filter(({ good }) => (good.receivedQuantity || 0) > 0 || good.adjustmentKind)
    : allGoods;
  const visibleDocuments = docs.map((doc) => ({ doc, goods: rows.filter((row) => row.doc.id === doc.id && row.doc.lineId === doc.lineId) }))
    .filter(({ goods }) => !onlyReceived || goods.length > 0);
  const notArrived = allGoods.filter(({ good }) => good.billingBasis === 'RECEIVED' && (good.receivedQuantity || 0) === 0 && !good.adjustmentKind).length;
  const adjustments = allGoods.filter(({ good }) => !!good.adjustmentKind).length;
  const amount = d.summary.billAmount ?? docs.reduce((total, doc) => total + num(doc.amount), 0);
  const count = (key: 'cashAmount' | 'cashBalanceAmount') => d.summary[key] === undefined ? '—' : money(d.summary[key]);

  return <div className={s.layout}>
    <main className={s.main}>
      <Alert tone="danger" title="供应商版按已入库数量计价，不是按采购量">
        客户版按已发货数量计价，供应商版按实际入库计价。未到货部分列出但不计金额，拒收和退货冲减本期。
      </Alert>
      <section className={s.card}>
        <header className={s.cardHead}>
          <div><h2>货物明细</h2><span>按采购单分组，组尾小计</span></div>
          <button type="button" aria-pressed={onlyReceived} onClick={() => setOnlyReceived((value) => !value)}>{onlyReceived ? '显示全部货物' : '只看已入库'}</button>
        </header>
        <div className={s.tableScroll}><table>
          <colgroup><col className={s.goodsCol}/><col className={s.codeCol}/><col className={s.packCol}/><col className={s.qtyCol}/><col className={s.qtyCol}/><col className={s.priceCol}/><col className={s.amountCol}/></colgroup>
          <thead><tr><th>货物</th><th>产品编号</th><th>包装方案</th><th>采购量</th><th>已入库</th><th>单价</th><th>金额 / 备注</th></tr></thead>
          {visibleDocuments.map(({ doc, goods: visible }) => {
            const subtotal = visible.reduce((total, row) => total + num(row.good.amount), 0);
            return <tbody key={doc.lineId || doc.id}>
              <tr className={s.docHead}><td colSpan={7}><b>{doc.documentNo}</b><span>· 下单 {shortDate(doc.documentDate)}{doc.dueDate ? ` · 到货 ${shortDate(doc.dueDate)}` : ''}</span>{(() => { const state = documentStatus(doc.targetType, doc.status || doc.note); return state && <i data-tone={state.tone}>{state.label}</i>; })()}</td></tr>
              {visible.length ? visible.map(({ good }, index) => <tr key={`${good.code || good.name}-${index}`}>
                <td>{good.name}</td><td className="tnum">{good.code || '—'}</td><td>{good.packaging || '—'}</td>
                <td className="tnum">{good.adjustmentKind ? '—' : good.orderedQuantity ?? good.quantity ?? '—'}</td>
                <td className={`tnum ${good.adjustmentKind ? s.negative : ''}`}>{good.receivedQuantity ?? '—'}</td>
                <td className="tnum">{good.unitPrice === undefined ? '—' : money(good.unitPrice)}</td>
                <td><b className={`tnum ${num(good.amount) < 0 ? s.negative : ''}`}>{good.billingBasis === 'RECEIVED' && !good.receivedQuantity && !good.adjustmentKind ? '不计本期' : money(good.amount)}</b><small>{good.adjustmentKind === 'REJECTION_GROSS' ? '拒收相关入库' : good.adjustmentKind === 'REJECTION_CONTRA' ? '拒收冲减' : good.adjustmentKind === 'RETURN' ? '入库后退货' : good.billingBasis === 'RECEIVED' && !good.receivedQuantity ? '未到货，顺延下期' : good.billingBasis === 'RECEIVED' ? '按已入库计' : '历史计价口径'}</small></td>
              </tr>) : <tr><td colSpan={7} className={s.emptyRow}>筛选后没有已入库货物</td></tr>}
              <tr className={s.subtotal}><td colSpan={6}>{doc.documentNo} 小计</td><td className="tnum">{money(subtotal)}</td></tr>
            </tbody>;
          })}
        </table></div>
        <footer className={s.total}><span>{visibleDocuments.length} 张采购单 · {rows.length} 个明细行 · {notArrived} 行未到货不计本期</span><b>本期应付 <strong className="tnum">{money(amount)}</strong></b></footer>
      </section>
      <section className={s.card}>
        <header className={s.cardHead}><div><h2>计价口径</h2><span>与客户版的差别，签合同时要写清</span></div></header>
        <div className={s.rules}>
          <div><span>计价基准</span><b>按已入库数量计价 · 未到货列出但不计金额</b></div>
          <div><span>拒收</span><b>拒收及其冲减按实际业务流水分行列示</b></div>
          <div><span>包装方案</span><b>展示下单时保存的包装方案快照</b></div>
          <div><span>改价</span><b>按本对账快照固化的采购单价结算</b></div>
          <div><span>退货</span><b>入库后退货在本期以负数冲减</b></div>
        </div>
      </section>
    </main>
    <aside className={s.aside}>
      <section className={s.card}>
        <header className={s.cardHead}><div><h2>与客户版的差别</h2></div></header>
        <table className={s.compare}><thead><tr><th></th><th>客户版</th><th>供应商版</th></tr></thead><tbody>
          <tr><td>计价</td><td>已发货</td><td><b>已入库</b></td></tr><tr><td>分组</td><td>销售订单</td><td><b>采购单</b></td></tr><tr><td>冲减</td><td>破损 / 退货</td><td><b>拒收 / 退货</b></td></tr><tr><td>套装</td><td>按套计价</td><td><b>不适用</b></td></tr><tr><td>包装</td><td>不显示</td><td><b>显示方案</b></td></tr>
        </tbody></table>
      </section>
      <section className={s.card}>
        <header className={s.cardHead}><div><h2>快照统计</h2></div></header>
        <dl className={s.stats}><div><dt>采购单</dt><dd>{docs.length}</dd></div><div><dt>明细行</dt><dd>{allGoods.length}</dd></div><div><dt>未到货行</dt><dd>{notArrived}</dd></div><div><dt>冲减行</dt><dd>{adjustments}</dd></div><div><dt>本期应付</dt><dd className={s.big}>{money(amount)}</dd></div><div><dt>已付</dt><dd>{count('cashAmount')}</dd></div><div><dt>未付</dt><dd>{count('cashBalanceAmount')}</dd></div></dl>
      </section>
    </aside>
  </div>;
}
